import {
  convertToVideoQuality,
  isVideoQuality
} from '@/common/helpers/chart-util';
import { fillInMissingDates } from '@/features/unit-tests-view/helpers/UnitTests.helpers';
import { dateParse } from '@samknows/utils';
import deepEqual from 'fast-deep-equal';
import cloneDeep from 'lodash.clonedeep';
import Vue from 'vue';
import {
  AnalyticsDataList,
  AnalyticsFormData,
  HighChartYAxisMetricUnit,
  MetricKeys,
  MeasurementBuilderFormData
} from '../global.interfaces';
import splitSort from './split-sort';

export function subtitleText(
  chartMetaArray: MeasurementBuilderFormData[]
): string {
  if (chartMetaArray.length === 2) {
    const title = Vue.t(`metrics.${chartMetaArray[1].metrics[0].key}.name`);
    const metricName = Vue.t('components.molecules.BaseGraph.andSomething', {
      something: title
    });

    if (chartMetaArray[1].plotValue !== 'metricValue') {
      const plotValue = Vue.t(`common.columns.${chartMetaArray[1].plotValue}`);
      return `${metricName} - ${plotValue}`;
    }

    return metricName;
  }

  if (chartMetaArray.length > 2) {
    return Vue.t('components.molecules.BaseGraph.andXOtherSeries', {
      x: chartMetaArray.length - 1
    });
  }

  return '';
}

// Note: this function mutates data as well as returning axes
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */ // TODO [WEBPLAT-151] add input and output types
export function generateYAxes(data: any): any[] {
  return data.reduce((axes, series) => {
    // If there are multiple metrics we only need to look at the first one -
    // they will share an axis anyway
    const metric = series.formData.metrics[0];

    const yLabelKey = metric
      ? `metrics.${metric.key}.label`
      : 'common.labelValue';
    let yLabel = Vue.t(yLabelKey);
    if (yLabel === yLabelKey) {
      yLabel = Vue.t('common.labelValue');
    }

    const plotValue = series.formData.plotValue || 'metricValue';
    const isCount = plotValue.endsWith('Count');

    // If the "Value to plot" option, configured per series by the user, is not
    // the default value of 'metricValue' ("Mean") which appears on an axis as
    // ${value} (measurement)` [e.g. "Speed (Mbps)"]:
    //   - If the"sampleCount" nor "agentCount", the yAxis label is whatever
    //     has been retrieved from our translation files. (e.g. "Sample Count")
    //   - Otherwise, take the translated metric key and append the plotValue
    //     label to it [e.g. "Speed - Standard Deviation (Mpbs)"]
    if (plotValue !== 'metricValue') {
      const label = Vue.t(`common.columns.${plotValue}`);

      if (isCount) {
        yLabel = label;
      } else {
        yLabel = yLabel.replace(/ *(\(|$)/, ` - ${label} $1`);
      }
    }

    let axisIndex = axes.findIndex(
      (s: { yLabel: string; metric: { unit: string } }) =>
        s.yLabel === yLabel && (isCount || s.metric.unit === metric.unit)
    );

    if (axisIndex === -1) {
      const axis = {
        metric,
        yLabel,
        unit: isCount ? '' : metric.unit
      };

      axisIndex = axes.push(axis) - 1;
    }

    series.yAxis = axisIndex;
    return axes;
  }, []);
}

/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO [WEBPLAT-151] add input and output types
export function generateMultiMetricChartData(data: any): {
  series: any[];
  axes: any[];
} {
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const axes = [];
  let series = cloneDeep(data);

  series = series.map((item) => {
    // If there are multiple metrics we only need to look at the first one -
    // they will share an axis anyway
    const metric = item.formData.metrics[0];
    const yLabelKey = metric
      ? `metrics.${metric.key}.label`
      : 'common.labelValue';
    let yLabel = Vue.t(yLabelKey);
    if (yLabel === yLabelKey) {
      yLabel = Vue.t('common.labelValue');
    }

    const plotValue = item.formData.plotValue || 'metricValue';
    const isCount = plotValue.endsWith('Count');

    // If the "Value to plot" option, configured per series by the user, is not
    // the default value of 'metricValue' ("Mean") which appears on an axis as
    // ${value} (measurement)` [e.g. "Speed (Mbps)"]:
    //   - If the"sampleCount" nor "agentCount", the yAxis label is whatever
    //     has been retrieved from our translation files. (e.g. "Sample Count")
    //   - Otherwise, take the translated metric key and append the plotValue
    //     label to it [e.g. "Speed - Standard Deviation (Mpbs)"]
    if (plotValue !== 'metricValue') {
      const label = Vue.t(`common.columns.${plotValue}`);

      if (isCount) {
        yLabel = label;
      } else {
        yLabel = yLabel.replace(/ *(\(|$)/, ` - ${label} $1`);
      }
    }

    let axisIndex = axes.findIndex(
      (s: HighChartYAxisMetricUnit) =>
        s.yLabel === yLabel && (isCount || s.metric.unit === metric.unit)
    );

    if (axisIndex === -1) {
      const axis = {
        metric,
        yLabel,
        unit: isCount ? '' : metric.unit
      };

      axisIndex = axes.push(axis) - 1;
    }

    item.yAxis = axisIndex;

    return item;
  });

  return {
    series,
    axes
  };
}

/**
 * This is the title used when dealing with a series from the form data - so
 * apollo form and CSV downloading.
 */
export function titleForSeriesSet(
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */ // TODO [WEBPLAT-151] add types for series
  series: any,
  formDataArray: AnalyticsFormData[]
): string {
  const title = [];

  if (series.metrics.length && series.metrics[0]) {
    const key = series.metrics[0].key || series.metrics[0];
    title.push(Vue.t(`metrics.${key}.name`));
  } else {
    title.push(Vue.t('components.molecules.EnterpriseForm.seriesNoMetrics'));
  }

  const hasMultipleTypes = formDataArray.some(
    ({ chartType }) => chartType !== series.chartType
  );

  if (hasMultipleTypes) {
    title.push(Vue.t(`common.chartTypes.${series.chartType}`));
  }

  if (series.plotValue !== 'metricValue') {
    title.push(Vue.t(`common.columns.${series.plotValue}`));
  }

  return title.join(' - ');
}

/**
 * This is the title when dealing with a series from response data.
 */
export function titleForSeries(
  dataset: AnalyticsDataList,
  meta: MeasurementBuilderFormData,
  metaArray: MeasurementBuilderFormData[]
): string {
  let name = [];

  if (metaArray && metaArray.length > 1) {
    name.push(Vue.t(`metrics.${meta.metrics[0].key}.name`));

    const index = metaArray.findIndex((item) => {
      return deepEqual(meta, item);
    });

    name[0] = `#${index + 1}: ${name[0]}`;
  }

  if (dataset.splitGroup) {
    name.push(...dataset.splitGroup.map((group) => group.splitLabel));
  }

  if (meta.plotValue !== 'metricValue') {
    name.push(Vue.t(`common.columns.${meta.plotValue}`));
  }

  name = name.filter((part) => part);

  if (name.length) {
    let joinedName = name.join(' - ');
    if (!dataset.metricData?.length) {
      joinedName = `${joinedName} (No Data)`;
    }

    return joinedName;
  }

  return Vue.t('common.other');
}

export function highchartsChartType(chartType: string): string {
  const highchartsChartTypes: Record<string, string> = {
    raw: 'scatter',
    aggregate: 'line'
  };
  return highchartsChartTypes[chartType] || chartType;
}

/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO [WEBPLAT-151] add input and output types
function metricDataToHighcharts(
  metricData: any[] = [],
  meta: any,
  metricKey: MetricKeys
): any {
  const isVideoQualityCached = isVideoQuality(meta.metrics[0].key);

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */ // TODO [WEBPLAT-151] add types for item and return types
  const getYValue = (item: any): any => {
    let value;
    if (meta.plotValue === 'iqr') {
      value = item.perc_75 - item.perc_25;
    } else if (metricKey === MetricKeys.ACTIVE_AGENT_COUNT) {
      value = item.agentCount;
    } else {
      value = item[meta.plotValue || 'metricValue'];
    }

    return isVideoQualityCached &&
      !(meta.plotValue && meta.plotValue.endsWith('Count'))
      ? convertToVideoQuality(value, meta.metrics[0].key)
      : value;
  };

  const isXofY = meta.timeAggregation.includes('_of_');
  const seriesData = metricData.map((item) => ({
    x: isXofY ? item.aggregatedNumber : dateParse(item.dtime),
    y: getYValue(item),
    data: item
  }));

  return {
    data: seriesData
  };
}
/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO [WEBPLAT-151] add input and output types
export function dataToSeries(
  data: any,
  formData: MeasurementBuilderFormData[],
  showMissingDays?: boolean
): any {
  const unsortedSeries = data.map((series) => {
    const meta = series.formData;

    const timePeriod = meta?.timeAggregation;
    const fromToDates = meta?.date;
    const isHourly = timePeriod === 'hourly';
    let metricDataRaw = series.metricData;
    const metricKey = series.metricMetadata?.metricKey;

    if (!isHourly && showMissingDays) {
      metricDataRaw = fillInMissingDates(series.metricData, fromToDates);
    }

    const singleSeries = {
      type: highchartsChartType(meta.chartType),
      showInLegend: meta.splitData,
      turboThreshold: 0,
      boostThreshold: 1000,
      canViewPoint: false,
      ...series,
      yAxis: series.yAxis,
      metricData: undefined,
      ...metricDataToHighcharts(metricDataRaw, meta, metricKey)
    };

    if (singleSeries.data.length === 1) {
      singleSeries.marker = { enabled: true };
    }

    if (meta.chartType === 'raw') {
      singleSeries.type = 'line';
      singleSeries.lineWidth = 0;
      singleSeries.states = {
        hover: {
          lineWidthPlus: 0
        }
      };
      singleSeries.marker = { enabled: true, radius: 2 };
      singleSeries.findNearestPointBy = 'xy';
    }

    return singleSeries;
  });

  const multiMetric = unsortedSeries.some((series) => series.series > 0);
  return (
    splitSort(unsortedSeries, 'name')
      // This reducer basically defines what the colours should be - when a
      // chart has multiple formDatas, each series has its own colour, otherwise
      // each chart series has its own colour
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */ // TODO [WEBPLAT-151] add types for series
      .reduce((seriesSet, series: any, i: number) => {
        const previousSeries = i ? seriesSet[seriesSet.length - 1] : undefined;
        const previousSeriesNum = previousSeries ? previousSeries.series : -1;

        let seriesNum = previousSeriesNum;
        if (
          !multiMetric ||
          (multiMetric && series.series > previousSeriesNum)
        ) {
          seriesNum++;
        }
        series.series = seriesNum;

        return seriesSet.concat(series);
      }, [])
  );
}
